/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo, useMemo } from 'react';
// eslint-disable-next-line jira/restricted/redux
// eslint-disable-next-line jira/restricted/redux
// eslint-disable-next-line jira/restricted/redux
import type { Store } from 'redux';
import { useMatch } from '@jira/navigation-apps__routing-hooks/src/useMatch.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { State } from '@jira/portfolio-3__portfolio/src/app-simple-plans/state/types.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { Skeleton } from './skeleton/index.tsx';

const LazyPlanSettingsSidebarWithRelay = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-plan-settings" */ './PlanSettingsSidebarLegacy.tsx'
		).then(({ PlanSettingsSidebarWithRelay }) => PlanSettingsSidebarWithRelay),
	{
		entryFsSsrLiftImportToModule: false, // can't be true yet as plans use window.location.pathname at module level & create redux reducers at module level - both not import lifting friendly
		includeInShellOnlySsrBundle: true,
	},
);

const LazyPlanSettingsSidebar = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-plan-settings-legacy" */ './PlanSettingsSidebarLegacy.tsx'
		).then(({ PlanSettingsSidebarLegacy }) => PlanSettingsSidebarLegacy),
	{
		entryFsSsrLiftImportToModule: false, // can't be true yet as plans use window.location.pathname at module level & create redux reducers at module level - both not import lifting friendly
		includeInShellOnlySsrBundle: true,
	},
);

type Props = {
	store?: Store<State>;
};

const toPlanAri = ({
	siteId,
	activationId,
	planId,
}: {
	siteId: string;
	activationId: string;
	planId: string;
}): string => `ari:cloud:jira:${siteId}:plan/activation/${activationId}/${planId}`;

// When plan_settings_sidebar_migrate_to_relay is ON, render the Relay-backed sidebar
// Rename to AsyncSidebar when cleanup plan_settings_sidebar_migrate_to_relay FG
const AsyncSidebarNew = ({ store }: Props): React.JSX.Element => {
	const { params } = useMatch();
	const { cloudId, activationId } = useTenantContext();

	const { planId, scenarioId } = params;
	const planIdAri = useMemo(
		() =>
			toPlanAri({
				siteId: cloudId,
				activationId,
				planId: String(planId),
			}),
		[cloudId, activationId, planId],
	);

	return (
		<Placeholder name="planSettingsSidebar" fallback={<Skeleton />}>
			<LazyPlanSettingsSidebarWithRelay
				store={store}
				planIdAri={planIdAri}
				planId={String(planId)}
				scenarioId={String(scenarioId)}
			/>
		</Placeholder>
	);
};

const AsyncSidebarLegacy = ({ store }: Props): React.JSX.Element => (
	<Placeholder name="asyncSidebarPlanSettings" fallback={<Skeleton />}>
		<LazyPlanSettingsSidebar store={store} />
	</Placeholder>
);

const AsyncSidebar = componentWithFG(
	'plan_settings_sidebar_migrate_to_relay',
	AsyncSidebarNew,
	AsyncSidebarLegacy,
);

const ExportedComponent: React.NamedExoticComponent<Props> = memo(AsyncSidebar);
export default ExportedComponent;
